import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

/**
 * plugins/echo.js — Realtime broadcasting bootstrap (Laravel Echo + Reverb).
 * ─────────────────────────────────────────────────────────────────
 * Generic connection layer for the authenticated-staff private channel.
 * Actual channel names / events are defined per-feature when that feature is
 * built (e.g. Notification Engine, Module 07) — this file only manages the
 * Echo instance and its auth handshake, via composables/usePrivateChannel.js.
 *
 * Registered automatically by @core/utils/plugins.js's `import.meta.glob`
 * (eager) — has no default export, so it's a no-op as a Vue plugin; its only
 * import-time side effect is `window.Pusher = Pusher`.
 *
 * Uses Laravel Reverb (self-hosted, Pusher-protocol-compatible), NOT Pusher
 * Cloud — so `wsHost`/`wsPort` point at our own `php artisan reverb:start`
 * process (VITE_REVERB_*), not a `cluster`-derived Pusher Cloud hostname.
 */

window.Pusher = Pusher

const REVERB_KEY = import.meta.env.VITE_REVERB_APP_KEY
const REVERB_HOST = import.meta.env.VITE_REVERB_HOST || window.location.hostname
const REVERB_PORT = import.meta.env.VITE_REVERB_PORT || 8080
const REVERB_SCHEME = import.meta.env.VITE_REVERB_SCHEME || 'http'
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

let echoInstance = null

/**
 * Inisialisasi Echo instance untuk sisi staf (user login, auth:sanctum).
 * Dipanggil sekali setelah login berhasil, dan di-disconnect saat logout.
 */
export function initEcho() {
  if (echoInstance) return echoInstance

  if (!REVERB_KEY) {
    console.warn('[echo] VITE_REVERB_APP_KEY belum di-set — realtime dinonaktifkan.')

    return null
  }

  echoInstance = new Echo({
    broadcaster: 'reverb',
    key: REVERB_KEY,
    wsHost: REVERB_HOST,
    wsPort: REVERB_PORT,
    wssPort: REVERB_PORT,
    forceTLS: REVERB_SCHEME === 'https',
    enabledTransports: ['ws', 'wss'],
    authEndpoint: `${API_BASE_URL}/broadcasting/auth`,
    authorizer: channel => ({
      // Backend auth:sanctum pakai bearer token (lihat utils/api.js), BUKAN
      // cookie/session — `credentials: 'include'` saja TIDAK cukup di sini,
      // Authorization header wajib diisi manual seperti $api.
      authorize: (socketId, callback) => {
        const accessToken = useCookie('accessToken').value

        fetch(`${API_BASE_URL}/broadcasting/auth`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            socket_id: socketId,
            channel_name: channel.name,
          }),
        })
          .then(res => {
            if (!res.ok) throw new Error(`Broadcast auth gagal (${res.status})`)

            return res.json()
          })
          .then(data => callback(null, data))
          .catch(err => callback(err, null))
      },
    }),
  })

  return echoInstance
}

export function getEcho() {
  return echoInstance
}

export function disconnectEcho() {
  echoInstance?.disconnect()
  echoInstance = null
}
